const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const MAPBOX_SEARCH_URL = 'https://api.mapbox.com/search/searchbox/v1';

const DEFAULT_CONFIG = {
  country: 'mx',
  language: 'es',
  limit: 5,
};

/**
 * Buscar direcciones por texto (autocompletado)
 * @param {string} query - Texto a buscar
 * @param {object} options - Opciones adicionales
 * @returns {Promise<Array>} - Lista de sugerencias
 */
export const searchAddress = async (query, options = {}) => {
  if (!query || query.length < 3) return [];

  try {
    const params = new URLSearchParams({
      q: query,
      access_token: MAPBOX_TOKEN,
      session_token: getSessionToken(),
      country: options.country || DEFAULT_CONFIG.country,
      language: options.language || DEFAULT_CONFIG.language,
      limit: options.limit || DEFAULT_CONFIG.limit,
      types: 'address,poi,place,neighborhood',
    });

    // Agregar bbox solo si el caller lo pide explícitamente (sin default fijo)
    if (options.bbox) {
      params.append('bbox', options.bbox);
    }

    // Agregar proximidad solo si el caller la pasa explícitamente (sin sesgo geográfico fijo)
    if (options.proximity) {
      params.append('proximity', options.proximity);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(
      `${MAPBOX_SEARCH_URL}/suggest?${params}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('Error en búsqueda de Mapbox');
    }

    const data = await response.json();

    return data.suggestions?.map(suggestion => ({
      id: suggestion.mapbox_id,
      name: suggestion.name,
      fullAddress: suggestion.full_address || suggestion.place_formatted,
      address: suggestion.address,
      city: suggestion.context?.place?.name,
      region: suggestion.context?.region?.name,
      country: suggestion.context?.country?.name,
      coordinates: null, // Se obtienen con retrieve()
      raw: suggestion,
    })) || [];

  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Timeout en búsqueda de dirección');
      return [];
    }
    console.error('Error buscando dirección:', error);
    return [];
  }
};

/**
 * Obtener detalles completos de una dirección (incluyendo coordenadas)
 * @param {string} mapboxId - ID de Mapbox de la sugerencia
 * @returns {Promise<object|null>} - Dirección completa con coordenadas
 */
export const retrieveAddress = async (mapboxId) => {
  if (!mapboxId) return null;

  try {
    const params = new URLSearchParams({
      access_token: MAPBOX_TOKEN,
      session_token: getSessionToken(),
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(
      `${MAPBOX_SEARCH_URL}/retrieve/${mapboxId}?${params}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('Error obteniendo detalles de dirección');
    }

    const data = await response.json();
    const feature = data.features?.[0];

    if (!feature) return null;

    return {
      id: feature.properties.mapbox_id,
      name: feature.properties.name,
      fullAddress: feature.properties.full_address || feature.properties.place_formatted,
      street: feature.properties.address || feature.properties.name,
      city: feature.properties.context?.place?.name,
      region: feature.properties.context?.region?.name,
      country: feature.properties.context?.country?.name,
      postalCode: feature.properties.context?.postcode?.name,
      coordinates: {
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
      },
      raw: feature,
    };

  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Timeout obteniendo detalles de dirección');
      return null;
    }
    console.error('Error obteniendo detalles:', error);
    return null;
  }
};

/**
 * Geocodificación inversa (coordenadas → dirección)
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {Promise<object|null>} - Dirección encontrada
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const params = new URLSearchParams({
      access_token: MAPBOX_TOKEN,
      language: 'es',
      limit: 1,
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(
      `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&${params}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('Error en geocodificación inversa');
    }

    const data = await response.json();
    const feature = data.features?.[0];

    if (!feature) return null;

    return {
      fullAddress: feature.properties.full_address || feature.properties.place_formatted,
      street: feature.properties.context?.address?.name || feature.properties.name,
      city: feature.properties.context?.place?.name,
      region: feature.properties.context?.region?.name,
      coordinates: {
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
      },
    };

  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Timeout en geocodificación inversa');
      return null;
    }
    console.error('Error en geocodificación inversa:', error);
    return null;
  }
};

// Generar un session token para agrupar requests (mejora pricing)
let sessionToken = null;
const getSessionToken = () => {
  if (!sessionToken) {
    sessionToken = crypto.randomUUID();
  }
  return sessionToken;
};

// Resetear session token (llamar cuando el usuario complete una búsqueda)
export const resetSessionToken = () => {
  sessionToken = null;
};

// ── ETA de manejo (Mapbox Directions) ──────────────────────────────────────
// Tiempo real por carretera entre origen (restaurante) y destino (usuario).
// Single-restaurant: el origen es fijo, así que cacheamos por coords del
// destino redondeadas (~1m). El restaurante no se mueve → caché largo (24h).
const ETA_CACHE_PREFIX = 'eta:';
const ETA_TTL_MS = 24 * 60 * 60 * 1000;
const round5 = (n) => Math.round(n * 1e5) / 1e5;

/**
 * Minutos de manejo del origen al destino. Cacheado en localStorage.
 * @param {{lat:number,lng:number}} origin  coords del restaurante
 * @param {{lat:number,lng:number}} dest    coords del usuario
 * @returns {Promise<number|null>} minutos (redondeados) o null si falla
 */
export const getDrivingEtaMinutes = async (origin, dest) => {
  if (!origin || !dest || !MAPBOX_TOKEN) return null;
  const key = `${ETA_CACHE_PREFIX}${round5(dest.lat)},${round5(dest.lng)}`;
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    if (cached && Date.now() - cached.ts < ETA_TTL_MS && typeof cached.min === 'number') {
      return cached.min;
    }
  } catch { /* cache corrupto: ignorar */ }

  try {
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/` +
      `${origin.lng},${origin.lat};${dest.lng},${dest.lat}` +
      `?access_token=${MAPBOX_TOKEN}&overview=false`;
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const seconds = data?.routes?.[0]?.duration;
    if (typeof seconds !== 'number') return null;
    const min = Math.max(1, Math.round(seconds / 60));
    try { localStorage.setItem(key, JSON.stringify({ min, ts: Date.now() })); } catch { /* quota */ }
    return min;
  } catch {
    return null;
  }
};
